import React from "react";
import clsx from "clsx";

import styles from "./sso-button.module.scss";

export interface SSOButtonProps {
  /** Provider label, e.g. "Continue with Google". */
  label: string;
  /**
   * Icon — either an asset path (SVG / PNG file) which is rendered as
   * an ``<img>``, or a React node (typically an inline SVG component
   * like ``EnvelopeIcon``) rendered directly. Used so the method-chooser
   * can render an OTP row + SSO rows with identical visual treatment.
   */
  icon: string | React.ReactNode;
  /** Renders as `<a href={...}>` for OAuth IdP redirects. If omitted, renders a button. */
  href?: string;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  /** When loading, dims the button and disables interaction. */
  loading?: boolean;
  /** When another SSO button on the page is loading. Greys this one out. */
  disabled?: boolean;
  className?: string;
  /** Alt text for the icon (only applied when ``icon`` is a string). Defaults to the label. */
  iconAlt?: string;
}

export const SSOButton: React.FC<SSOButtonProps> = ({
  label,
  icon,
  href,
  onClick,
  loading = false,
  disabled = false,
  className,
  iconAlt,
}) => {
  const isInactive = loading || disabled;

  const content = (
    <>
      <span className={styles["sso-icon-wrapper"]} aria-hidden={loading}>
        {typeof icon === "string" ? (
          <img
            className={styles["sso-icon"]}
            src={icon}
            alt={iconAlt || label}
          />
        ) : (
          icon
        )}
      </span>
      <span className={styles["sso-label"]}>{label}</span>
      {loading && (
        <span
          data-testid="auth-sso-spinner"
          className={clsx("spinner-border", styles["sso-spinner"])}
        />
      )}
    </>
  );

  const classes = clsx(
    styles["sso-button"],
    isInactive && styles["sso-button-inactive"],
    loading && styles["sso-button-loading"],
    className
  );

  if (href && !isInactive) {
    return (
      <a className={classes} href={href} onClick={onClick}>
        {content}
      </a>
    );
  }

  return (
    <button
      type="button"
      className={classes}
      onClick={onClick}
      disabled={isInactive}
    >
      {content}
    </button>
  );
};

export default SSOButton;
